import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import { createAlert } from "../scripts/createAlert"
import initializeBootstrapTooltips from "../scripts/tooltips"

export default class extends Controller {
  static targets = ["form", "questionsContainer", "question", "sortable", "optionsSection", "linearScaleSection", "dataTypeSection", "normalizationSection", "exampleSection", "descriptionSection", "addOptionButton"]
  static values = {
    formId: Number,
    predefinedOptionExamples: Object,
  }

  connect() {
    this.setupCsrfForJquery()
    this.initializeSortable()
    this.initializeOptionSortables()
    this.refreshQuestionSections()
    this.updateQuestionNumbers()
  }

  setupCsrfForJquery() {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    if (!token) return

    $.ajaxSetup({
      beforeSend: (xhr) => {
        xhr.setRequestHeader("X-CSRF-Token", token)
      },
    })
  }

  initializeSortable() {
    this.sortableTargets.forEach((elm) => {
      Sortable.create(elm, {
        draggable: ".question-sortable",
        handle: ".sortable-handle",
        filter: ".forms-admin-question-options-list, .form-option-row, .forms-admin-question-option-handle",
        preventOnFilter: false,
        onUpdate: (event) => {
          this.updateQuestionNumbers()
          this.updateQuestionPositions()
          const rows = event.target.querySelectorAll(".question-sortable")
          const orderedIds = Array.from(rows)
            .map((elm2) => elm2.dataset.attachmentId)
            .filter(Boolean)

          if (orderedIds.length === 0) return

          $.post(`/forms/${this.formIdValue}/reorder`, { ordered_question_ids: orderedIds }).then(() => {
            createAlert("success", "Question order updated")
          })
        },
      })
    })
  }

  addQuestion() {
    fetch(`/forms/${this.formIdValue}/questions/new`)
      .then((res) => res.text())
      .then((partial) => {
        this.questionsContainerTarget.insertAdjacentHTML("beforeend", partial);
        initializeBootstrapTooltips()
        this.initializeOptionSortables()
        this.refreshQuestionSections()
        this.updateQuestionNumbers()
      });
  }

  attemptSubmit(event) {
    event.preventDefault()

    let valid = true
    let errorMessage

    this.questionTargets.forEach((question) => {
      const questionText = question.querySelector(".question-text")
      if (!questionText) return

      if (questionText.value.trim() === "") {
        questionText.classList.add("is-invalid")
        valid = false
        errorMessage = "Please make sure all questions have text before saving."
      } else {
        questionText.classList.remove("is-invalid")
      }
    })

    if (!valid) {
      createAlert("danger", errorMessage)
      return
    }

    if (!this.formTarget.reportValidity()) return

    this.updateQuestionPositions()
    this.updateAllOptionPositions()
    this.formTarget.submit()
  }

  initializeOptionSortables() {
    this.element.querySelectorAll('[data-forms-questions-target="optionsContainer"]').forEach((container) => {
      if (container._formsOptionSortable) {
        container._formsOptionSortable.destroy()
        container._formsOptionSortable = null
      }

      container._formsOptionSortable = Sortable.create(container, {
        draggable: ".form-option-row",
        handle: ".forms-admin-question-option-handle",
        animation: 150,
        filter: "input, textarea, select, button, a",
        preventOnFilter: false,
        onEnd: (event) => {
          if (event.oldIndex === event.newIndex) return

          this.updateOptionPositions(container)
          this.persistOptionOrder(container)
        },
      })

      this.updateOptionPositions(container)
    })
  }

  addOption(event) {
    event.preventDefault()
    const questionNode = event.currentTarget.closest(".question-sortable")
    if (this.hasPredefinedOptionsSelected(questionNode)) return
    const addOptionButton = questionNode.querySelector('[data-forms-questions-target="nestedAddOptionButton"]')
    if (addOptionButton) {
      addOptionButton.click()
      requestAnimationFrame(() => {
        const container = questionNode.querySelector('[data-forms-questions-target="optionsContainer"]')
        const formatSelect = questionNode.querySelector('select[name*="[format]"]')
        const selectManyFormat = this.normalizeFormatValue(formatSelect?.value) === "select_many"
        this.initializeOptionSortables()
        this.updateOptionPositions(container)
        this.updateOptionMarkers(questionNode, selectManyFormat)
        const optionInputs = questionNode.querySelectorAll('input[data-option-text-input="true"]')
        const lastOptionInput = optionInputs[optionInputs.length - 1]
        if (lastOptionInput) lastOptionInput.focus()
      })
    }
  }

  removeOption(event) {
    event.preventDefault()
    const row = event.currentTarget.closest(".form-option-row")
    const destroyField = row.querySelector(".option-destroy-flag")
    const idField = row.querySelector('input[name*="[id]"]')

    if (idField) {
      destroyField.value = "1"
      row.classList.add("d-none")
    } else {
      row.remove()
    }

    const container = row.closest('[data-forms-questions-target="optionsContainer"]')
    this.updateOptionPositions(container)
  }

  syncOptionText(event) {
    const row = event.currentTarget.closest(".form-option-row")
    if (!row) return

    const nextValue = event.currentTarget.value
    const labelInput = row.querySelector('input[data-option-label-input="true"]')
    const valueInput = row.querySelector('input[data-option-value-input="true"]')

    if (labelInput) labelInput.value = nextValue
    if (valueInput) valueInput.value = nextValue
  }

  clearQuestionTextInvalid(event) {
    if (event.currentTarget.value.trim() !== "") {
      event.currentTarget.classList.remove("is-invalid")
    }
  }

  formatChanged(event) {
    const questionNode = event.currentTarget.closest(".question-sortable")
    this.refreshQuestionSection(questionNode)
  }

  predefinedOptionsChanged(event) {
    const questionNode = event.currentTarget.closest(".question-sortable")
    const optionsSection = questionNode?.querySelector('[data-forms-questions-target="optionsSection"]')
    const excludedContainer = optionsSection?.querySelector('[data-predefined-options-excluded-container="true"]')
    if (excludedContainer) excludedContainer.innerHTML = ""
    this.refreshQuestionSection(questionNode)
  }

  removePredefinedOption(event) {
    event.preventDefault()
    const questionNode = event.currentTarget.closest(".question-sortable")
    const optionsSection = questionNode?.querySelector('[data-forms-questions-target="optionsSection"]')
    const excludedContainer = optionsSection?.querySelector('[data-predefined-options-excluded-container="true"]')
    if (!excludedContainer) return

    const optionValue = event.currentTarget.dataset.optionValue
    if (!optionValue) return

    const fieldName = excludedContainer.dataset.excludedFieldName
    if (!fieldName) return

    const existingValues = Array.from(excludedContainer.querySelectorAll('input[type="hidden"]')).map((input) => input.value)
    if (!existingValues.includes(optionValue)) {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = fieldName
      input.value = optionValue
      excludedContainer.appendChild(input)
    }

    this.refreshQuestionSection(questionNode)
  }

  deleteUnsavedQuestion(event) {
    event.preventDefault();
    const question = event.target.closest(".question-sortable");
    if (question) question.remove();
    this.updateQuestionNumbers()
  }

  refreshQuestionSections() {
    this.questionTargets.forEach((questionNode) => this.refreshQuestionSection(questionNode))
  }

  updateQuestionNumbers() {
    this.questionTargets.forEach((questionNode, index) => {
      const numberNode = questionNode.querySelector("[data-question-number]")
      if (numberNode) numberNode.textContent = `${index + 1}`
    })
  }

  updateQuestionPositions() {
    if (!this.hasQuestionsContainerTarget) return

    const groupPositions = {}
    this.questionsContainerTarget.querySelectorAll(".question-sortable").forEach((row) => {
      const positionInput = row.querySelector('input[data-question-position-input="true"]')
      if (!positionInput) return

      const groupKey = row.dataset.questionGroupId || "ungrouped"
      groupPositions[groupKey] = (groupPositions[groupKey] || 0) + 1
      positionInput.value = groupPositions[groupKey]
    })
  }

  updateAllOptionPositions() {
    this.element.querySelectorAll('[data-forms-questions-target="optionsContainer"]').forEach((container) => {
      this.updateOptionPositions(container)
    })
  }

  refreshQuestionSection(questionNode) {
    if (!questionNode) return
    const formatSelect = questionNode.querySelector('select[name*="[format]"]')
    const formatInput = formatSelect || questionNode.querySelector(".question-format")
    const formatValue = formatInput?.value
    const optionsSection = questionNode.querySelector('[data-forms-questions-target="optionsSection"]')
    const linearScaleSection = questionNode.querySelector('[data-forms-questions-target="linearScaleSection"]')
    const dataTypeSection = questionNode.querySelector('[data-forms-questions-target="dataTypeSection"]')
    const normalizationSection = questionNode.querySelector('[data-forms-questions-target="normalizationSection"]')
    const exampleSection = questionNode.querySelector('[data-forms-questions-target="exampleSection"]')
    const descriptionSection = questionNode.querySelector('[data-forms-questions-target="descriptionSection"]')
    const dateTimeSection = questionNode.querySelector('[data-forms-questions-target="dateTimeSection"]')
    const exampleEnabledCheckbox = questionNode.querySelector('input[type="checkbox"][name*="[example_enabled]"]')
    const exampleField = questionNode.querySelector('input[name*="[example]"], textarea[name*="[example]"]')
    const descriptionToggle = questionNode.querySelector('input[data-description-toggle="true"]')
    const descriptionField = questionNode.querySelector('textarea[name*="[description]"], input[name*="[description]"]:not([type="hidden"])')

    const normalizedFormat = this.normalizeFormatValue(formatValue)
    const selectFormat = normalizedFormat === "select_one" || normalizedFormat === "select_many"
    const predefinedSelected = selectFormat && this.hasPredefinedOptionsSelected(questionNode)
    const predefinedSelect = questionNode.querySelector('select[name*="[predefined_option_key]"]')
    if (!selectFormat && predefinedSelect && predefinedSelect.value) {
      predefinedSelect.value = ""
      const excludedContainer = optionsSection?.querySelector('[data-predefined-options-excluded-container="true"]')
      if (excludedContainer) excludedContainer.innerHTML = ""
    }

    if (optionsSection) optionsSection.classList.toggle("d-none", !selectFormat || predefinedSelected)
    if (dataTypeSection) dataTypeSection.classList.toggle("d-none", !selectFormat)

    const linearScale = normalizedFormat === "linear_scale"
    if (linearScaleSection) linearScaleSection.classList.toggle("d-none", !linearScale)
    const textFormat = normalizedFormat === "short_answer" || normalizedFormat === "paragraph"
    const selectManyFormat = normalizedFormat === "select_many"
    if (normalizationSection) {
      normalizationSection.classList.toggle("d-none", !textFormat)
      normalizationSection.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
        checkbox.disabled = !textFormat
      })
    }

    const showDescription = Boolean(descriptionToggle?.checked)
    if (descriptionSection) descriptionSection.classList.toggle("d-none", !showDescription)
    if (descriptionField) descriptionField.disabled = !showDescription

    const dateTimeFormat = normalizedFormat === "date_time"
    if (dateTimeSection) {
      dateTimeSection.classList.toggle("d-none", !dateTimeFormat)
      dateTimeSection.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
        checkbox.disabled = !dateTimeFormat
      })
    }

    const showExample = textFormat && Boolean(exampleEnabledCheckbox?.checked)
    if (exampleSection) exampleSection.classList.toggle("d-none", !showExample)
    if (exampleField) exampleField.disabled = !showExample
    if (optionsSection) {
      optionsSection.querySelectorAll(".form-option-input").forEach((input) => {
        if (predefinedSelected) {
          input.disabled = true
        } else {
          input.removeAttribute("disabled")
        }
      })
      optionsSection.querySelectorAll('[data-predefined-options-add-button="true"], [data-option-remove-button="true"]').forEach((elm) => {
        elm.classList.toggle("d-none", predefinedSelected)
      })
      optionsSection.querySelectorAll('[data-predefined-options-message="true"]').forEach((elm) => {
        elm.classList.toggle("d-none", !predefinedSelected)
      })
    }

    this.updateOptionMarkers(questionNode, selectManyFormat)
    this.renderPredefinedOptionsPreview(questionNode, predefinedSelected)
  }

  normalizeFormatValue(formatValue) {
    if (formatValue === null || formatValue === undefined) return ""
    const raw = String(formatValue)
    const map = {
      "0": "short_answer",
      "1": "paragraph",
      "2": "select_many",
      "3": "select_one",
      "4": "date_time",
      "5": "linear_scale",
      "6": "file_upload",
    }
    return map[raw] || raw
  }

  hasPredefinedOptionsSelected(questionNode) {
    if (!questionNode) return false
    const predefinedSelect = questionNode.querySelector('select[name*="[predefined_option_key]"]')
    return Boolean(predefinedSelect && predefinedSelect.value)
  }

  renderPredefinedOptionsPreview(questionNode, predefinedSelected) {
    const examplesSection = questionNode?.querySelector('[data-predefined-options-examples="true"]')
    const examplesContent = examplesSection?.querySelector('[data-predefined-options-example-content="true"]')
    if (!examplesSection || !examplesContent) return

    const predefinedSelect = questionNode.querySelector('select[name*="[predefined_option_key]"]')
    const key = predefinedSelect?.value
    const exampleText = predefinedSelected && key ? this.predefinedOptionExamplesValue?.[key] : ""

    examplesContent.textContent = exampleText ? `Examples: ${exampleText}` : ""
    examplesSection.classList.toggle("d-none", !exampleText)
  }

  updateOptionMarkers(questionNode, selectManyFormat) {
    questionNode?.querySelectorAll('[data-option-marker="true"]').forEach((marker) => {
      marker.classList.toggle("forms-admin-question-option-marker--checkbox", selectManyFormat)
      marker.classList.toggle("forms-admin-question-option-marker--radio", !selectManyFormat)
    })
  }

  updateOptionPositions(container) {
    if (!container) return

    let position = 1
    container.querySelectorAll(".form-option-row").forEach((row) => {
      const positionInput = row.querySelector('input[data-option-position-input="true"]')
      if (!positionInput) return
      if (row.classList.contains("d-none")) return

      positionInput.value = position
      position += 1
    })
  }

  persistOptionOrder(container) {
    if (!container) return

    const sanitizeId = (value) => String(value || "").replace(/"/g, "")

    const questionIdRaw =
      container.dataset.questionId ||
      container.closest(".question-sortable")?.getAttribute("data-attachment-id")

    const questionId = sanitizeId(questionIdRaw)
    if (!questionId) return

    const visibleRows = Array.from(container.querySelectorAll(".form-option-row:not(.d-none)"))
    const orderedOptionIds = visibleRows
      .map((row) => row.dataset.optionId || row.getAttribute("data-option-id"))
      .map(sanitizeId)
      .filter(Boolean)

    if (orderedOptionIds.length === 0 || orderedOptionIds.length !== visibleRows.length) return

    $.post(`/forms/${this.formIdValue}/questions/${questionId}/reorder_options`, {
      ordered_option_ids: orderedOptionIds,
    })
      .done(() => {
        createAlert("success", "Option order updated")
      })
      .fail(() => {
        createAlert("danger", "Unable to update option order")
      })
  }

  decorateOptionLabel(typeLabel, optionValue) {
    const label = String(typeLabel || "").trim()
    const value = String(optionValue || "")
    if (!label) return value

    return `${label} (${value})`
  }
}
